import React, { Component, Fragment } from 'react';

import { fabric } from 'fabric';
import CanvasHelper from 'helpers/Canvas';
import { setThumborSignedURL } from 'helpers/ImageFile';
import { isBlank } from 'helpers/String';
import { FileService } from 'modules/file';
import { ENTITY_TYPE, UPLOAD_STATUS } from 'modules/file/constants';
import { getFileLink, waitAsyncImage } from 'modules/file/helpers';
import { UiService } from 'modules/ui';
import PropTypes from 'prop-types';

import CanvasBackground from './CanvasBackground';
import CanvasInnerImage from './CanvasInnerImage';

import { UploadManager } from '@hotmart/club-files-ui';

export default class CanvasImages extends Component {
  static propTypes = {
    canvasRef: PropTypes.object,
    onLoading: PropTypes.func,
    updateInfo: PropTypes.func
  };

  state = {
    backgroundLoading: false,
    innerImageLoading: false,
    [ENTITY_TYPE.CERTIFICATE_BACKGROUND_IMG]: {
      nFiles: 0
    },
    [ENTITY_TYPE.CERTIFICATE_IMG]: {
      nFiles: 0
    }
  };

  initialState = [];

  setBackgroundImageInCanvasRef = (canvasRef, img) => {
    const backgroundConfigs = CanvasHelper.getBackgroundConfigToFitCanvas(
      img,
      canvasRef
    );

    img.set(backgroundConfigs);

    canvasRef.setBackgroundImage(img, canvasRef.renderAll.bind(canvasRef));
  };

  loadBackgroundFromURL = (canvasRef, src) => {
    if (isBlank(src)) {
      return;
    }

    const thumborSignedSrc = setThumborSignedURL(src);

    fabric.Image.fromURL(thumborSignedSrc, (img) =>
      this.setBackgroundImageInCanvasRef(canvasRef, img)
    );
  };

  addImageInCanvasRef(canvasRef, img) {
    const { width, height } = img;
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const halfWidth = canvasRef.width / width / 2;
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const halfHeigth = canvasRef.height / height / 2;
    const maxScale = Math.min(halfHeigth, halfWidth);

    img.set({
      scaleX: maxScale,
      scaleY: maxScale
    });

    canvasRef.add(img);
    canvasRef.renderAll();
  }

  loadImageFromURL = (canvasRef, src) => {
    if (isBlank(src)) {
      return;
    }

    const thumborSignedSrc = setThumborSignedURL(src);

    fabric.Image.fromURL(thumborSignedSrc, (img) =>
      this.addImageInCanvasRef(canvasRef, img)
    );
  };

  onMediaChanged = (files = [], onItemAddCallback, type) => {
    const lastUploadedFile = files[files.length - 1] || {};
    const hasChangedFiles =
      this.state[type] && files.length !== this.state[type].nFiles;

    if (hasChangedFiles && lastUploadedFile.status === UPLOAD_STATUS.FINISHED) {
      const { id } = lastUploadedFile;
      const { canvasRef, onLoading } = this.props;
      const url = getFileLink(id);

      const onLoadErrorCallback = () => {
        UiService.setAlertError('error_try_again');
        onLoading(false);
      };

      const onLoadSuccessCallback = () => {
        if (url) {
          onLoading(false);
          this.setState({ [type]: { nFiles: files.length } });
          onItemAddCallback(canvasRef, url);
          this.props.updateInfo();
        }
      };

      waitAsyncImage(url, onLoadSuccessCallback, onLoadErrorCallback);
    }
  };

  onBackgroundImageChange = (files) => {
    this.onMediaChanged(
      files,
      this.loadBackgroundFromURL,
      ENTITY_TYPE.CERTIFICATE_BACKGROUND_IMG
    );
  };

  onInnerImageChange = (files) => {
    this.onMediaChanged(
      files,
      this.loadImageFromURL,
      ENTITY_TYPE.CERTIFICATE_IMG
    );
  };

  setLoading = (files) => {
    const { onLoading } = this.props;

    if (files[0]) {
      onLoading(files[0].isUploading);
    }
  };

  render() {
    const { canvasRef } = this.props;
    const { backgroundLoading, innerImageLoading } = this.state;

    return (
      <Fragment>
        <UploadManager
          entity={ENTITY_TYPE.CERTIFICATE_BACKGROUND_IMG}
          itemsProp="images"
          images={this.initialState}
          onItemsChange={this.onBackgroundImageChange}
          onItemsUpdateStatus={this.setLoading}
          onError={FileService.handleFileException}
        >
          <CanvasBackground
            disabled={backgroundLoading}
            canvasRef={canvasRef}
          />
        </UploadManager>

        <UploadManager
          entity={ENTITY_TYPE.CERTIFICATE_IMG}
          itemsProp="images"
          images={this.initialState}
          onItemsChange={this.onInnerImageChange}
          onItemsUpdateStatus={this.setLoading}
          onError={FileService.handleFileException}
        >
          <CanvasInnerImage
            disabled={innerImageLoading}
            canvasRef={canvasRef}
          />
        </UploadManager>
      </Fragment>
    );
  }
}
